// eslint-disable-next-line no-use-before-define
import React, { useEffect, useRef, useState, useContext } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { styled } from '@mui/system';
import { useQuery } from '@apollo/client';

import { Card, LoadingSpinner } from 'shared-components/components';
import SimpleTableFilter, { DATATYPE } from 'shared-components/components/UIFormComponents/SimpleTableFilter';

import { GET_PATIENT_DOCUMENTS, PREFETCH_PATIENT_DOCUMENTS } from './PatientProfileQueries';
import { PatientDocumentType } from './PatientSummaryInterfaces';
import useInterval from 'shared-components/utils/useInterval';
import { DocumentType } from './Summary/interfaces';
import { Warning } from 'shared-components/images';
import { UserContextType } from '../../../contexts/UserContext/UserContext';
import UserContext from '../../../contexts/UserContext/UserContext';
import { RoleType } from 'op-enums';
import { CurrentAppConfig } from '../Careplan/AppConfig';
import TooltipWarning from 'shared-components/components/TooltipWarning/TooltipWarning';
import { LARGE_DISPLAY_SIZE } from 'shared-components/enums';
import { Button, Stack, Typography } from '@mui/material';
import {
  FindInPageOutlined as FindInPageOutlinedIcon,
  UploadOutlined as UploadOutlinedIcon,
} from '@mui/icons-material';
import { normaliseDocuments, renderDocumentRowIcon, renderDocumentRowTooltip } from '../Documents/DocumentsList';

const StyledDocListCol = styled(Stack)`
  color: ${(props) => props.theme.palette.text.primary};
  height: 100%;
  overflow: auto;
  padding: 0px;
  justify-content: center;
  td {
    font-size: 14px;
  }
  table {
    margin: 0;
  }
`;

const StyledContainer = styled(Card)`
  height: 50%;
  padding: 16px;
  tbody tr:hover {
    cursor: pointer;
  }
  display: flex;
  flex-direction: column;
`;

const StyledSimpleTable = styled(SimpleTableFilter)`
  line-height: 40px;
  margin: 25px 5px;
  height: 100%;
  width: 100%;
`;

const UploadIcon = styled(UploadOutlinedIcon)`
  width: 24px;
  height: 24px;
  margin-right: -5px;
`;

const WarningIcon = styled(Warning)`
  width: 20px;
  height: 20px;
  margin-right: 5px;
  margin-left: 3px;
`;

interface ROPatientDocsListProps {
  setDocument: (document: PatientDocumentType | null) => void;
  triggerModal: any;
  isSmallDisplay: boolean;
  currentDocument: DocumentType | null;
  setShowDocumentUploadPage: (showPage: boolean) => void;
  unsubmittedDocuments: boolean;
  disableUpload: boolean;
}

const ROPatientDocList = (props: ROPatientDocsListProps): JSX.Element => {
  const match = useRouteMatch<any>();
  // const currentDocument = props.currentDocument as PatientDocumentType;
  const [selectedDocument, setSelectedDocument] = useState<PatientDocumentType | null>();
  const { state } = useContext<UserContextType>(UserContext);
  const { PSO, RO } = RoleType;
  const [documents, setDocuments] = useState<PatientDocumentType[]>([]);
  const docsListRef = useRef<HTMLDivElement>(null);
  const { id: patientId } = match.params;

  const { data, loading, refetch } = useQuery(GET_PATIENT_DOCUMENTS, {
    variables: { patientId: patientId },
    skip: !patientId,
    fetchPolicy: 'cache-and-network',
  });
  /* Refresh documents list every 15 seconds */
  useInterval(() => {
    refetch();
  }, 15000);

  useQuery(PREFETCH_PATIENT_DOCUMENTS, {
    variables: { patientId: patientId },
    skip: !patientId,
    fetchPolicy: 'cache-and-network',
  });

  const tableHeader = [
    { header: 'Type', key: 'title', sort: false, type: DATATYPE.STRING },
    { header: 'Date', key: 'dateOfDocument', sort: true, type: DATATYPE.DATE },
  ];
  const heading = 'Documents';

  const documentSelectCallback = (id: string | null): void => {
    const doc = id != null ? documents.filter((doc) => doc.id === id)[0] : null;
    props.setDocument(doc);
    setSelectedDocument(doc);
  };

  const handleOnRowClick = (id: string) => {
    documentSelectCallback(id);
    props.isSmallDisplay && props.triggerModal();
  };

  /* Update local documents and document type filters when data is refreshed */
  useEffect(() => {
    if (data && data.documents) {
      setDocuments(normaliseDocuments(data.documents));
    }
  }, [data]);

  useEffect(() => {
    if (documents?.length && !selectedDocument && !props.isSmallDisplay) {
      documentSelectCallback(documents[0].id);
    }
  }, [documents]);

  useEffect(() => {
    if (!props.currentDocument) {
      setSelectedDocument(null);
    }
  }, [props.currentDocument]);

  return (
    <>
      <StyledContainer>
        <Stack justifyContent="space-between" alignItems="center" direction="row" paddingBottom={1}>
          <Typography variant="h6">{heading}</Typography>
          {(state.primaryRole === PSO ||
            (state.primaryRole === RO && CurrentAppConfig.PatientSummary.RODocumentUpload)) && (
            <TooltipWarning
              disabled={!(props.disableUpload || props.unsubmittedDocuments)}
              message={
                props.disableUpload
                  ? 'Syncing patient with Mosaiq, please try again soon...'
                  : props.unsubmittedDocuments
                  ? 'Unsubmitted documents'
                  : ''
              }>
              <Button
                onClick={(): void => {
                  if (!props.disableUpload) {
                    props.setShowDocumentUploadPage(true);
                  }
                }}
                variant="text"
                id="upload-document-block"
                disabled={props.disableUpload}
                startIcon={<UploadIcon color="primary" />}
                endIcon={props.unsubmittedDocuments ? <WarningIcon /> : <></>}>
                Upload document
              </Button>
            </TooltipWarning>
          )}
        </Stack>
        <StyledDocListCol ref={docsListRef}>
          {loading ? (
            <LoadingSpinner loadingText={'Loading Documents'} relativeSpinner={true} />
          ) : (
            <StyledSimpleTable
              header={heading}
              onRowClick={(id: string) => handleOnRowClick(id)}
              dataProps={tableHeader}
              data={documents}
              partitionNeedsAttentionFirst
              documentEditModes={{}}
              selectedRow={selectedDocument ? selectedDocument.id : null}
              emptyStateMessage="No documents available"
              renderRowIcon={renderDocumentRowIcon}
              renderRowTooltip={renderDocumentRowTooltip}
              renderRowStatus={(doc: PatientDocumentType) => {
                if (doc.status === 'Draft') {
                  return (
                    <Typography variant="caption" color="warning.dark">
                      Draft
                    </Typography>
                  );
                }
                if (doc.hasUncommittedChanges) {
                  return (
                    <Typography variant="caption" color="warning.dark">
                      Unsubmitted changes
                    </Typography>
                  );
                }
                return null;
              }}
            />
          )}
        </StyledDocListCol>
      </StyledContainer>
    </>
  );
};

export default ROPatientDocList;
